import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { ArrowLeft, Upload, X } from 'lucide-react';

const NuevoGasto = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categorias, setCategorias] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [formData, setFormData] = useState({
    monto: '',
    concepto: '',
    descripcion: '',
    categoria_id: '',
    fecha_gasto: new Date().toISOString().split('T')[0],
    tipo_gasto: 'unico',
    ligado_a: 'ninguno',
    contrato_id: '',
    propietario_id: '',
    // Recurrente fields
    frecuencia: 'mensual',
    dia_generacion: '1',
    fecha_inicio: new Date().toISOString().split('T')[0],
    fecha_fin: ''
  });
  const [archivos, setArchivos] = useState([]);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const categoriasRes = await fetch(`${API_URL}/api/gastos/categorias`, { headers });
      if (categoriasRes.ok) setCategorias(await categoriasRes.json());

      const contratosRes = await fetch(`${API_URL}/api/contracts?limit=500`, { headers });
      if (contratosRes.ok) setContratos(await contratosRes.json());
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setArchivos([...archivos, ...files]);
  };

  const removeFile = (index) => {
    setArchivos(archivos.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      // Create gasto
      const response = await fetch(`${API_URL}/api/gastos`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Error al crear el gasto');
      }

      // Upload files
      if (archivos.length > 0) {
        for (const archivo of archivos) {
          const formDataFile = new FormData();
          formDataFile.append('archivo', archivo);

          await fetch(`${API_URL}/api/gastos/${data.id}/archivos`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formDataFile
          });
        }
      }

      alert('Gasto creado exitosamente');
      navigate('/egresos');
    } catch (error) {
      console.error('Error:', error);
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => navigate('/egresos')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Volver a Egresos
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Nuevo Gasto</h1>
          <p className="text-gray-600 mt-1">Registra un nuevo gasto o crea uno recurrente</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
          {/* Tipo de Gasto */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Gasto</label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="unico"
                  checked={formData.tipo_gasto === 'unico'}
                  onChange={(e) => setFormData({ ...formData, tipo_gasto: e.target.value })}
                  className="mr-2"
                />
                Una sola vez
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="recurrente"
                  checked={formData.tipo_gasto === 'recurrente'}
                  onChange={(e) => setFormData({ ...formData, tipo_gasto: e.target.value })}
                  className="mr-2"
                />
                Recurrente
              </label>
            </div>
          </div>

          {/* Información básica */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Monto *</label>
              <input
                type="number"
                step="0.01"
                value={formData.monto}
                onChange={(e) => setFormData({ ...formData, monto: e.target.value })}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Categoría *</label>
              <select
                value={formData.categoria_id}
                onChange={(e) => setFormData({ ...formData, categoria_id: e.target.value })}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="">Selecciona una categoría</option>
                {categorias.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.nombre}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Concepto *</label>
            <input
              type="text"
              value={formData.concepto}
              onChange={(e) => setFormData({ ...formData, concepto: e.target.value })}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="Ej: Cambio de boiler, Comisión por renta"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
            <textarea
              value={formData.descripcion}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="Detalle adicional del gasto..."
            />
          </div>

          {formData.tipo_gasto === 'unico' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha del Gasto *</label>
              <input
                type="date"
                value={formData.fecha_gasto}
                onChange={(e) => setFormData({ ...formData, fecha_gasto: e.target.value })}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
          )}

          {/* Campos para gastos recurrentes */}
          {formData.tipo_gasto === 'recurrente' && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-4">
              <h3 className="font-semibold text-gray-900">Configuración Recurrente</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Frecuencia *</label>
                  <select
                    value={formData.frecuencia}
                    onChange={(e) => setFormData({ ...formData, frecuencia: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="mensual">Mensual</option>
                    <option value="bimestral">Bimestral</option>
                    <option value="trimestral">Trimestral</option>
                    <option value="anual">Anual</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Día de generación *</label>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={formData.dia_generacion}
                    onChange={(e) => setFormData({ ...formData, dia_generacion: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de inicio *</label>
                  <input
                    type="date"
                    value={formData.fecha_inicio}
                    onChange={(e) => setFormData({ ...formData, fecha_inicio: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de término (opcional)</label>
                <input
                  type="date"
                  value={formData.fecha_fin}
                  onChange={(e) => setFormData({ ...formData, fecha_fin: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
                <p className="text-xs text-gray-500 mt-1">Deja vacío para que el gasto se genere indefinidamente</p>
              </div>
            </div>
          )}

          {/* Ligado a */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Ligado a</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="ninguno"
                  checked={formData.ligado_a === 'ninguno'}
                  onChange={(e) => setFormData({ ...formData, ligado_a: e.target.value, contrato_id: '', propietario_id: '' })}
                  className="mr-2"
                />
                Ninguno (General)
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="contrato"
                  checked={formData.ligado_a === 'contrato'}
                  onChange={(e) => setFormData({ ...formData, ligado_a: e.target.value, propietario_id: '' })}
                  className="mr-2"
                />
                Contrato
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="propietario"
                  checked={formData.ligado_a === 'propietario'}
                  onChange={(e) => setFormData({ ...formData, ligado_a: e.target.value, contrato_id: '' })}
                  className="mr-2"
                />
                Propietario
              </label>
            </div>

            {formData.ligado_a === 'contrato' && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Seleccionar Contrato *</label>
                <select
                  value={formData.contrato_id}
                  onChange={(e) => setFormData({ ...formData, contrato_id: e.target.value })}
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">Selecciona un contrato</option>
                  {contratos.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.numero_contrato} - {c.inmueble_direccion}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {formData.ligado_a === 'propietario' && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Seleccionar Propietario *</label>
                <select
                  value={formData.propietario_id}
                  onChange={(e) => setFormData({ ...formData, propietario_id: e.target.value })}
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">Selecciona un propietario</option>
                  {contratos.map(c => (
                    <option key={c.propietario_email} value={c.propietario_email}>
                      {c.propietario_nombre}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Archivos */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Adjuntar Documentos</label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
              <input
                type="file"
                onChange={handleFileChange}
                multiple
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer flex flex-col items-center"
              >
                <Upload className="h-10 w-10 text-gray-400 mb-2" />
                <p className="text-sm text-gray-600">Click para seleccionar archivos</p>
                <p className="text-xs text-gray-500 mt-1">PDF, imágenes, documentos</p>
              </label>
            </div>

            {archivos.length > 0 && (
              <div className="mt-4 space-y-2">
                {archivos.map((file, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                    <span className="text-sm text-gray-700">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => navigate('/egresos')}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Creando...' : 'Crear Gasto'}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default NuevoGasto;